import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody } from "reactstrap";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  updateLocalSettingsMasterConfigToggle,
  updateLocalSettingsMasterConfigToggleSuccess,
  updateLocalSettingsMasterConfigUser,
  updateLocalSettingsMasterConfigUserSuccess,
} from "../../../store/generalSettings/action";
import {
  FEEDBACK_TOOL_CONFIG_ID,
  FEEDBACK_TOOL_HIDE,
  FEEDBACK_TOOL_SHOW,
  patchFeedbackToolMode,
  readFeedbackToolMode,
} from "../../../helpers/functions/feedbackTool";

// Task 24733 - the on/off switch for the floating "Report a problem" button.
//
// One component, two scopes:
//   scope="company" - Company Settings. Turning it OFF also clears
//                     ConfigCompany.FlagAllowUserOverride, so nobody in the
//                     company can switch it back on for themselves; turning it
//                     ON restores the override so individuals may still opt out.
//   scope="user"    - User Profile. Writes a ConfigUser row for this staff
//                     member only. It cannot un-hide a company that has the
//                     tool off, because that company blocked the override.
//
// Both write the same MasterConfigItem (56) through the existing
// configcompany/append and configuser/append endpoints, and both patch
// localStorage so the button appears or disappears without a re-login.
export default function FeedbackToolToggle({ scope = "company" }) {
  const dispatch = useDispatch();
  const isCompanyScope = scope === "company";

  const [enabled, setEnabled] = useState(
    () => readFeedbackToolMode() !== FEEDBACK_TOOL_HIDE
  );

  const { companySaveResponse, userSaveResponse } = useSelector((state) => ({
    companySaveResponse:
      state.GeneralSettings.updateLocalSettingsMasterConfigToggleSuccess,
    userSaveResponse:
      state.GeneralSettings.updateLocalSettingsMasterConfigUserSuccess,
  }));

  const handleChange = (nextEnabled) => {
    if (nextEnabled === enabled) return;
    setEnabled(nextEnabled);

    const value = nextEnabled ? FEEDBACK_TOOL_SHOW : FEEDBACK_TOOL_HIDE;

    if (isCompanyScope) {
      dispatch(
        updateLocalSettingsMasterConfigToggle([
          {
            MasterConfigItem_Id: FEEDBACK_TOOL_CONFIG_ID,
            // Off at company level is absolute - drop the per-staff override so
            // it cannot be undone one user at a time.
            ConfigCompany_FlagAllowUserOverride: nextEnabled ? "1" : "0",
            ConfigCompany_Value: value,
          },
        ])
      );
    } else {
      dispatch(
        updateLocalSettingsMasterConfigUser([
          {
            MasterConfigItem_Id: FEEDBACK_TOOL_CONFIG_ID,
            ConfigUser_Value: value,
          },
        ])
      );
    }

    patchFeedbackToolMode(value);
  };

  // Save feedback. Each scope watches only its own endpoint's response so the
  // two toggles never toast for each other.
  const saveResponse = isCompanyScope ? companySaveResponse : userSaveResponse;
  useEffect(() => {
    if (isEmpty(saveResponse)) return;
    if (saveResponse.status === 200) {
      toast.success("Feedback tool setting saved");
    } else {
      toast.error(saveResponse.message);
      // The write failed, so put the switch back where it was.
      setEnabled(readFeedbackToolMode() !== FEEDBACK_TOOL_HIDE);
    }
    dispatch(
      isCompanyScope
        ? updateLocalSettingsMasterConfigToggleSuccess([])
        : updateLocalSettingsMasterConfigUserSuccess([])
    );
  }, [saveResponse]);

  return (
    <Card className="mb-0">
      <CardBody>
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
          <div>
            <h6 className="mb-1">
              <i className="fa-solid fa-bug text-danger me-2" />
              Report a problem button
            </h6>
            <p className="text-muted mb-0 fs-13">
              {isCompanyScope
                ? "Shows a small bug button in the bottom-left corner for everyone in this company. They can grab the screen they are on, point at what is wrong, annotate it and send it straight to us. Turning it off here removes it for the whole company and staff cannot turn it back on."
                : "Shows a small bug button in the bottom-left corner so you can report a problem from any screen. Turning it off only affects you."}
            </p>
          </div>

          <div className="btn-group btn-group-sm flex-shrink-0" role="group">
            <button
              type="button"
              className={`btn ${enabled ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => handleChange(true)}
            >
              On
            </button>
            <button
              type="button"
              className={`btn ${
                !enabled ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => handleChange(false)}
            >
              Off
            </button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
